/**
 * Membership Plans API wrapper (v2 — Project Independence, DNKADM-0056 U1).
 *
 * **A plan IS the tier** (handoff §5.9). The v1 notion of a separate "membership
 * tier" string is gone: a `v2_membership_plan` row carries the tier name (mirrored to
 * the contact system's `membership_tier`), a term, and a price; everywhere v1 keyed off
 * a tier STRING, v2 keys off a **plan id**. This module owns the entity CRUD +
 * soft-delete/reactivate (§4.3) and
 * the canonical fail-closed {@link useMembershipPlanPickList} that daily caps (0057)
 * and members (0059) import.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.9, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/membership-plans?status=active|inactive|all` → {@link MembershipPlan}`[]`.
 *  - `GET    /v2/admin/membership-plans/{id}` → one {@link MembershipPlan}.
 *  - `POST   /v2/admin/membership-plans` ← {@link MembershipPlanInput} → the created row.
 *  - `PUT    /v2/admin/membership-plans/{id}` ← {@link MembershipPlanInput} → the updated row.
 *  - `POST   /v2/admin/membership-plans/{id}/publish` → take a Draft plan live (DNKAPI-0261).
 *  - `POST   /v2/admin/membership-plans/{id}/copy` → deep-clone a plan into a new Draft.
 *  - `POST   /v2/admin/membership-plans/{id}/deactivate` | `/reactivate` (§4.3).
 *
 * **`v2_membership_plan` field set (handoff §5.9; DNKAPI-0253/0256):** `name` (the tier
 * name, mirrored to `contact.membership_tier`; unique on `(tenant, name)`), `description`
 * (opt), `termDays`, `price` (DECIMAL(10,2) — plain decimal in the tenant currency, NOT
 * cents), `isActive`, and `locationIds` — the owned locations the plan is available at
 * (**`[]` ⇒ available at ALL locations**). The former `provider`/`providerProductId` PSP
 * fields were removed (DNKAPI-0256 — dink-api now resolves the payment plan by the plan's
 * own id; there is no PSP field to enter or show for a plan).
 *
 * Reads are parsed DEFENSIVELY (mirrors `services.ts` / `creditBuckets.ts`): a
 * missing/wrong-typed field degrades (an id-less row is dropped), never a throw.
 */
import { request } from './client';
import { useCallback } from 'react';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { PickListError } from './picklists';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin membership-plans base path (bearer surface). */
const PATH = '/v2/admin/membership-plans';

/** The three lifecycle states a plan can be in (DNKAPI-0261). */
type MembershipPlanStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

/**
 * One `v2_membership_plan` row (§5.9). `name` is the tier name (mirrored to the
 * contact system). `price` is a plain decimal in the tenant currency (NOT cents).
 * `locationIds` are the owned locations the plan is available at — **`[]` means
 * available at ALL locations** (DNKAPI-0253). `description` is omitted when absent
 * (exactOptionalPropertyTypes).
 */
export interface MembershipPlan {
  id: string;
  /** The tier name this plan grants (unique on `(tenant, name)`). */
  name: string;
  /** Membership length in days (integer). */
  termDays: number;
  /** Price (DECIMAL(10,2)) — a plain decimal in the tenant currency. */
  price: number;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /**
   * Lifecycle status (DNKAPI-0261): `DRAFT` (created, never published — hidden from
   * customers, not purchasable), `ACTIVE` (published and live), `INACTIVE` (deactivated/
   * soft-removed). Independent of `isActive`: a Draft is still `isActive:true` underneath;
   * `INACTIVE` and `isActive:false` move together once a plan has been published. For a
   * priced plan, pay-api registration happens at publish time, not create time (DNKAPI-0265
   * §5). Always present on a GET response.
   */
  status: MembershipPlanStatus;
  /** Owned `v2_location.id`s the plan is available at; `[]` ⇒ ALL locations. */
  locationIds: string[];
  /**
   * Credit refill mode for this plan's recurring credit buckets (DNKAPI-0259): `true`
   * = additive (each renewal stacks a fresh lot; leftovers carry until expiry); `false`
   * (default) = replace (zero the active lots, grant one fresh lot — no carryover).
   */
  creditsAdditive: boolean;
  /**
   * Who can buy the plan (DNKAPI-0535 / DNKADM-0381): `true` = staff only — the front desk
   * can still sell it, the member app never lists it; `false` (default) = members and staff.
   * Orthogonal to the lifecycle `status` and to `isActive`.
   */
  staffOnly: boolean;
  /**
   * Whether a subscription to this plan is taxed (DNKAPI-0335). Read so a row-level write can
   * send it back verbatim — the server's PUT reads an absent `taxable` as `false`.
   */
  taxable: boolean;
  description?: string;
  /**
   * Who is on the plan right now (DNKAPI-0562 / DNKADM-0407) — read-only, on the list and the
   * single-plan read. Omitted when the server sends none or sends it malformed: every consumer
   * treats an absent `impact` as "unknown" and shows nothing.
   */
  impact?: MembershipPlanImpact;
}

/**
 * The plan's current memberships (DNKAPI-0562): `activeMemberships` =
 * `billedByDink` (subscriptions billed through Dink) + `notBilledByDink` (comps and
 * staff-added memberships).
 */
export interface MembershipPlanImpact {
  activeMemberships: number;
  billedByDink: number;
  notBilledByDink: number;
}

/**
 * Create/update body for a plan. `name`/`termDays`/`price` are required by the form.
 * `locationIds` are the owned locations the plan is available at (`[]` ⇒ all
 * locations); the dialog always sends the current array. `isActive` is NOT settable
 * here — activation is the dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface MembershipPlanInput {
  name: string;
  termDays: number;
  price: number;
  locationIds?: string[];
  /** Credit refill mode (see {@link MembershipPlan.creditsAdditive}); the dialog always sends it. */
  creditsAdditive?: boolean;
  /** Who can buy (see {@link MembershipPlan.staffOnly}); absent leaves the server value unchanged. */
  staffOnly?: boolean;
  /** Tax toggle (see {@link MembershipPlan.taxable}); absent reads as `false` server-side. */
  taxable?: boolean;
  description?: string;
}

/** Options for {@link listMembershipPlans}. */
export interface ListMembershipPlansOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
  /**
   * Convenience flag (mirrors the spec's `listMembershipPlans({ includeInactive? })`):
   * `true` ⇒ `status=all`. Ignored when an explicit `status` is also passed.
   */
  includeInactive?: boolean;
}

/**
 * The minimal `{ id, name }` shape the shared {@link useMembershipPlanPickList}
 * selector returns — the canonical option downstream plan pickers bind (0057, 0059).
 */
export interface MembershipPlanOption {
  id: string;
  name: string;
}

// --- Defensive parsing helpers (mirrors services.ts / creditBuckets.ts) ---------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Parse a raw lifecycle status; anything unrecognized degrades to `ACTIVE` (never crashes). */
function asPlanStatus(value: unknown): MembershipPlanStatus {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'DRAFT') return 'DRAFT';
    if (upper === 'INACTIVE') return 'INACTIVE';
  }
  return 'ACTIVE';
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse the inline `locationIds` array off a plan row (DNKAPI-0253): each entry is a
 * v2 entity id (numeric-or-string on the wire), normalized through {@link asId},
 * dropping nulls and duplicates (order-preserving — mirrors `services.ts`'s
 * `extractIds` entry normalization, inline on the row). Absent/non-array/malformed →
 * `[]` (the safe default — "all locations" — never a throw; this is a display field).
 */
function parseLocationIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const entry of value) {
    const id = asId(entry);
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** A whole count `>= 0` (a finite non-negative integer), or null. */
function asCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/**
 * Parse the read-only `impact` block (DNKAPI-0562), or null when it is absent or any of its
 * three counts is missing/malformed — a half-parsed count would put a wrong number in front of
 * staff, so the whole block degrades to "unknown" instead.
 */
function parseImpact(value: unknown): MembershipPlanImpact | null {
  if (!isObject(value)) return null;
  const activeMemberships = asCount(value['activeMemberships']);
  const billedByDink = asCount(value['billedByDink']);
  const notBilledByDink = asCount(value['notBilledByDink']);
  if (activeMemberships === null || billedByDink === null || notBilledByDink === null) return null;
  return { activeMemberships, billedByDink, notBilledByDink };
}

/**
 * Parse one raw plan, or null when it has no usable `id` (an id-less row can't be
 * rendered or edited — drop it rather than surface a half-identified plan). A missing
 * `name` degrades to an empty string. `locationIds` degrades to `[]` (⇒ all locations)
 * when absent/malformed. `description` is assigned only when present
 * (exactOptionalPropertyTypes).
 */
function parsePlan(raw: unknown): MembershipPlan | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const plan: MembershipPlan = {
    id,
    name: asText(raw['name']) ?? '',
    termDays: asNumber(raw['termDays']) ?? 0,
    price: asNumber(raw['price']) ?? 0,
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    status: asPlanStatus(raw['status']),
    locationIds: parseLocationIds(raw['locationIds']),
    creditsAdditive: asBoolean(raw['creditsAdditive'], false),
    staffOnly: asBoolean(raw['staffOnly'], false),
    taxable: asBoolean(raw['taxable'], false),
  };
  const description = asText(raw['description']);
  if (description !== null) plan.description = description;
  const impact = parseImpact(raw['impact']);
  if (impact !== null) plan.impact = impact;
  return plan;
}

/**
 * Extract the plans array TOLERANTLY from a successful body, returning `null` when
 * NEITHER a bare array NOR a known wrapper key (`membershipPlans`/`plans`/`content`)
 * yields one. The list read turns `null` into `[]` (a real first-run state, §8); the
 * fail-closed pick-list turns it into a {@link PickListError}.
 */
function extractPlans(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['membershipPlans'])) return body['membershipPlans'];
    if (Array.isArray(body['plans'])) return body['plans'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/**
 * Build the JSON body for a create/update. `locationIds` is sent whenever supplied
 * (the dialog always sends the current array — `[]` for "all locations" or the
 * selected set); `description` is omitted when absent.
 */
function toBody(input: MembershipPlanInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    name: input.name,
    termDays: input.termDays,
    price: input.price,
  };
  if (input.locationIds !== undefined) json['locationIds'] = input.locationIds;
  if (input.creditsAdditive !== undefined) json['creditsAdditive'] = input.creditsAdditive;
  if (input.staffOnly !== undefined) json['staffOnly'] = input.staffOnly;
  if (input.taxable !== undefined) json['taxable'] = input.taxable;
  if (input.description !== undefined) json['description'] = input.description;
  return json;
}

function planPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/membership-plans?status=…` → the tenant's membership plans,
 * defensively parsed (a bad entry dropped). The `status` filter (§4.3) defaults to
 * `active` (live rows only); pass `status: 'inactive'|'all'` or `includeInactive: true`
 * to include deactivated rows. A body that yields no array degrades to `[]` (a real
 * first-run state, §8). Throws a typed `ApiError` on a non-2xx response.
 */
export async function listMembershipPlans(
  options: ListMembershipPlansOptions = {},
): Promise<MembershipPlan[]> {
  const status = options.status ?? (options.includeInactive ? 'all' : 'active');
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return (extractPlans(body) ?? [])
    .map(parsePlan)
    .filter((p): p is MembershipPlan => p !== null);
}

/**
 * `POST /v2/admin/membership-plans` → create a plan. Returns the re-parsed created
 * row. Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on the
 * unique `(tenant, name)` rule, upstream).
 */
export async function createMembershipPlan(
  input: MembershipPlanInput,
): Promise<MembershipPlan | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parsePlan(body);
}

/**
 * `PUT /v2/admin/membership-plans/{id}` → update a plan. Returns the re-parsed updated
 * row. Throws a typed `ApiError`.
 */
export async function updateMembershipPlan(
  id: string,
  input: MembershipPlanInput,
): Promise<MembershipPlan | null> {
  const body = await request<unknown>('PUT', planPath(id), { json: toBody(input) });
  return parsePlan(body);
}

/**
 * Set who can buy a plan straight from its list row (DNKADM-0381 U1): a
 * `PUT /v2/admin/membership-plans/{id}` carrying the row's current fields with `staffOnly`
 * set to `staffOnly`. The PUT is whole-row (the server requires `name` and reads an absent
 * `taxable` as `false`), so every field the row carries goes back verbatim — only
 * `staffOnly` changes. Returns the re-parsed row. Throws a typed `ApiError`.
 */
export async function setMembershipPlanStaffOnly(
  plan: MembershipPlan,
  staffOnly: boolean,
): Promise<MembershipPlan | null> {
  const input: MembershipPlanInput = {
    name: plan.name,
    termDays: plan.termDays,
    price: plan.price,
    locationIds: plan.locationIds,
    creditsAdditive: plan.creditsAdditive,
    taxable: plan.taxable,
    staffOnly,
  };
  if (plan.description !== undefined) input.description = plan.description;
  return updateMembershipPlan(plan.id, input);
}

/**
 * `POST /v2/admin/membership-plans/{id}/deactivate` → soft-deactivate a plan (§4.3 —
 * never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateMembershipPlan(id: string): Promise<void> {
  await request<unknown>('POST', `${planPath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/membership-plans/{id}/reactivate` → reactivate a soft-deactivated
 * plan (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateMembershipPlan(id: string): Promise<void> {
  await request<unknown>('POST', `${planPath(id)}/reactivate`);
}

/**
 * `POST /v2/admin/membership-plans/{id}/publish` → take a Draft plan live. No body.
 * Idempotent — publishing an already-published plan is a no-op that returns its current
 * state. For a priced plan (`price > 0`), this is also when pay-api registration happens
 * (DNKAPI-0265 §5) — the plan becomes billable. Returns the re-parsed updated row. Throws
 * a typed `ApiError`.
 */
export async function publishMembershipPlan(id: string): Promise<MembershipPlan | null> {
  const body = await request<unknown>('POST', `${planPath(id)}/publish`);
  return parsePlan(body);
}

/**
 * `POST /v2/admin/membership-plans/{id}/copy` → deep-clone a plan (credit deposit rules,
 * location assignments, plan-scoped daily caps) into a new Draft. `name` is OPTIONAL: omit
 * it (the one-click path) to let the backend auto-generate a unique `"<source> (Copy)"`
 * name — no body is sent; supply `name` to set your own (a colliding supplied name throws
 * `409 DUPLICATE_NAME`). Returns the re-parsed NEW plan. Throws a typed `ApiError`.
 */
export async function copyMembershipPlan(id: string, name?: string): Promise<MembershipPlan | null> {
  const body = await request<unknown>('POST', `${planPath(id)}/copy`, {
    ...(name !== undefined ? { json: { name } } : {}),
  });
  return parsePlan(body);
}

// --- The shared, fail-closed membership-plan pick-list --------------------------

/**
 * Load the active membership plans as `{ id, name }` options, **fail-closed**: any
 * upstream/network failure OR a malformed (non-array) `200` body throws the shared
 * {@link PickListError}, so the loader renders error + Retry and NEVER a silent empty
 * list (a failure can never masquerade as "loaded, empty"). A successful empty list
 * returns `[]` (a real first-run state). Only `active` plans are offered (a
 * deactivated plan must not be assignable to a member or a daily cap).
 */
export async function getMembershipPlanOptions(): Promise<MembershipPlanOption[]> {
  try {
    const body = await request<unknown>('GET', PATH, { query: { status: 'active' } });
    const list = extractPlans(body);
    if (list === null) throw new Error('expected an array of membership plans');
    return list
      .map(parsePlan)
      .filter((p): p is MembershipPlan => p !== null)
      .map((p) => ({ id: p.id, name: p.name }));
  } catch (cause) {
    throw new PickListError('membership-plans', cause);
  }
}

/**
 * `useMembershipPlanPickList()` — the **canonical** fail-closed membership-plan
 * selector downstream features import (daily caps 0057, members 0059). Loads live via
 * {@link getMembershipPlanOptions} through `usePickList` (fail-closed): `loading →
 * ready` with the `{ id, name }[]` options, or `loading → error + Retry` — never a
 * partial/empty-on-failure list. Render through `<PickListLoader>`. Name kept EXACTLY
 * — downstream specs import it verbatim.
 */
export function useMembershipPlanPickList(): PickList<MembershipPlanOption> {
  return usePickList(useCallback(() => getMembershipPlanOptions(), []), []);
}
